// Leadership.tsx
import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  FaBirthdayCake,
  FaUser,
  FaGraduationCap,
  FaHeart,
  FaHome,
  FaSchool,
  FaUsers,
  FaMosque,
  FaChild,
  FaArrowLeft,
  FaCalendarAlt,
  FaBook,
  FaHandsHelping
} from 'react-icons/fa';

const Leadership: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-slate-900 via-indigo-950 to-purple-900 pt-6 pb-8">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-full blur-3xl"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-6xl mx-auto"
          >
            <div className="text-center max-w-3xl mx-auto mb-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 backdrop-blur-sm text-cyan-300 rounded-full text-sm font-semibold mb-4">
                <FaUsers className="text-xs" />
                Leadership
              </div>

              <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
                Alhaji Inusah Adamu
              </h1>

              <p className="text-base md:text-lg text-gray-300">
                Honoring a century of service, philanthropy, and education
              </p>
            </div>
          </motion.div>

        </div>
      </div>

      {/* Main Content */}
      <div className="py-12 md:py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto mb-10 -mt-16 md:-mt-20">
            <div className="flex flex-col items-center gap-8">
              {/* Round Avatar */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6 }}
                className="relative"
              >
                <div className="w-48 h-48 md:w-64 md:h-64 rounded-full overflow-hidden border-8 border-white/20 shadow-2xl">
                  <img 
                    src="/alhaji.jpg" 
                    alt="Alhaji Inusah Adamu" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-6 py-2 rounded-full text-sm font-semibold shadow-lg">
                  (1913 - 2014)
                </div>
              </motion.div>
            </div>
          </div>
          <div className="max-w-6xl mx-auto">
            {/* Timeline Header */}
            <div className="mb-12 text-center">
              <h2 className="text-3xl font-bold text-slate-900 mb-4">The Inspiration Behind the Foundation</h2>
              <div className="w-24 h-1 bg-gradient-to-r from-cyan-500 to-blue-500 mx-auto"></div>
            </div>

            {/* Biography Content */}
            <div className="grid md:grid-cols-3 gap-8">
              {/* Left Column - Key Facts */}
              <div className="md:col-span-1">
                <motion.div 
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                  className="sticky top-24"
                >
                  <div className="bg-gradient-to-b from-cyan-50 to-white rounded-2xl shadow-lg p-6 border border-cyan-100">
                    <h3 className="text-xl font-bold text-slate-800 mb-6 flex items-center gap-2">
                      <FaUser className="text-cyan-600" />
                      Key Facts
                    </h3>
                    
                    <div className="space-y-6">
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <FaCalendarAlt className="w-4 h-4 text-cyan-600" />
                          <span className="font-semibold text-slate-700">Life Span</span>
                        </div>
                        <p className="text-slate-600 pl-7">12th March 1913 – 12th March 2014</p>
                      </div>

                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <FaHome className="w-4 h-4 text-cyan-600" />
                          <span className="font-semibold text-slate-700">Hometown</span>
                        </div>
                        <p className="text-slate-600 pl-7">Tumu ,Upper West (Ghana)</p>
                      </div>

                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <FaGraduationCap className="w-4 h-4 text-cyan-600" />
                          <span className="font-semibold text-slate-700">Profession</span>
                        </div>
                        <p className="text-slate-600 pl-7">Retired Miner & Philanthropist</p>
                      </div>

                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <FaChild className="w-4 h-4 text-cyan-600" />
                          <span className="font-semibold text-slate-700">Family</span>
                        </div>
                        <p className="text-slate-600 pl-7">18 Children (14 Surviving)</p>
                      </div>

                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <FaMosque className="w-4 h-4 text-cyan-600" />
                          <span className="font-semibold text-slate-700">Community Role</span>
                        </div>
                        <p className="text-slate-600 pl-7">Obuasi Muslim Council Member</p>
                      </div>

                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <FaSchool className="w-4 h-4 text-cyan-600" />
                          <span className="font-semibold text-slate-700">Educational Legacy</span>
                        </div>
                        <p className="text-slate-600 pl-7">Founding Member of Saqafiyya School</p>
                      </div>
                    </div>

                    {/* <div className="mt-8 pt-6 border-t border-cyan-100">
                      <Link 
                        to="/about" 
                        className="inline-flex items-center justify-center gap-2 w-full py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-xl font-medium hover:from-cyan-600 hover:to-blue-600 transition-all duration-300"
                      >
                        <FaHandsHelping className="w-4 h-4" />
                        See Foundation's Work
                      </Link>
                    </div> */}
                  </div>
                </motion.div>
              </div>

              {/* Right Column - Full Biography */}
              <div className="md:col-span-2">
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8 }}
                  className="bg-white rounded-2xl shadow-lg p-8 space-y-8 text-slate-800"
                >
                  <h2 className="text-2xl font-bold text-slate-900 mb-2 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-r from-cyan-100 to-blue-100 flex items-center justify-center">
                      <FaBook className="w-5 h-5 text-cyan-600" />
                    </div>
                    Profile of Alhaji Inusah Adamu
                  </h2>

                  <div className="leading-relaxed text-slate-700">
                    <p className="mb-4">
                      Alhaji Inusah Adamu was a retired miner, philanthropist, and a pillar of community development in Obuasi, Ghana. Born on 12th March 1913 in Tumu, he devoted his long and fruitful life to the service of humanity, education, and the spiritual upliftment of his community.
                    </p>

                    <h3 className="text-xl font-semibold mt-2 mb-2 flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-cyan-100 flex items-center justify-center">
                        <FaHeart className="w-4 h-4 text-cyan-600" />
                      </div>
                      A Life of Philanthropy
                    </h3>
                    <p className="mb-3">
                      After years of dedicated service as a miner, Alhaji Inusah Adamu turned his focus to philanthropy and community development. He had a deep conviction that education was the foundation of progress. While he ensured the education of his immediate family, his concern extended far beyond his household to the wider community.
                    </p>
                    <p className="mb-3">
                      He became a strong advocate for both Islamic and secular education, influencing and supporting initiatives that enabled many children and young people in Obuasi to gain access to learning opportunities. He was particularly passionate about supporting the less privileged. Through personal financial assistance, he sponsored the education of many needy students. His home was also a place of compassion and generosity, where hot daily meals were provided to members of the community until his passing.
                    </p>

                    <h3 className="text-xl font-semibold mt-4 mb-2 flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                        <FaGraduationCap className="w-4 h-4 text-blue-600" />
                      </div>
                      Contributions to Education
                    </h3>
                    <p className="mb-3">
                      Alhaji Inusah Adamu played an active role in community affairs and was a committed member of the Obuasi Muslim Council. Through his involvement, he contributed significantly to the establishment and growth of Muslim education in Obuasi, from the Central Mosque to New Nsuta. His efforts supported the founding of the Muslim Council Primary School, which was later renamed Awurade Basa L/A Primary and Middle School, Obuasi.
                    </p>
                    <p className="mb-3">
                      In addition, he was a founding member of Saqafiyya Primary and Junior High School in Obuasi. Demonstrating exceptional personal sacrifice, he hosted several Islamic tutors in his own home and assumed responsibility for their accommodation and salaries to ensure the sustainability of teaching and learning.
                    </p>

                    <h3 className="text-xl font-semibold mt-4 mb-2 flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                        <FaUsers className="w-4 h-4 text-purple-600" />
                      </div>
                      Legacy & Family
                    </h3>
                    <p className="mb-3">
                      Alhaji Inusah Adamu passed away on 12th March 2014, exactly one hundred years after his birth. His life was marked by humility, generosity, and unwavering dedication to the welfare of others. His legacy of kindness, service, and commitment to education continues to inspire generations and remains deeply cherished by the people of Obuasi.
                    </p>
                    <div className="mt-4 p-4 rounded-xl bg-cyan-50">
                      <p className="font-semibold text-slate-800">
                        Alhaji Inusah had eighteen (18) children, fourteen (14) of whom are alive and continue his legacy of philanthropy through the establishment of the foundation in his honour.
                      </p>
                    </div>

                    <div className="mt-6">
                      <h3 className="text-xl font-semibold mb-4 text-center">A Century Timeline</h3>
                      <div className="flex items-center justify-between">
                        <div className="text-center">
                          <div className="w-16 h-16 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center mx-auto mb-2">
                            <span className="text-xl font-bold">1913</span>
                          </div>
                          <p className="text-sm">Birth in Obuasi</p>
                        </div>
                        <div className="h-1 flex-1 bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-500 mx-4"></div>
                        <div className="text-center">
                          <div className="w-16 h-16 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center mx-auto mb-2">
                            <span className="text-xl font-bold">2014</span>
                          </div>
                          <p className="text-sm">Passing & Legacy</p>
                        </div>
                      </div>
                      <div className="text-center mt-6">
                        <p className="text-slate-600">A life of service spanning a full century</p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>

            {/* Call to Action */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="mt-16 text-center"
            >
              <div className="bg-gradient-to-r from-cyan-50 to-blue-50 rounded-2xl p-10 border border-cyan-100">
                <h3 className="text-2xl font-bold text-slate-800 mb-4">Continuing His Legacy</h3>
                <p className="text-slate-700 mb-8 max-w-2xl mx-auto">
                  The Alhaji Inusah Foundation Community Centre continues to honor his memory through educational programs, community development, and humanitarian support across Ghana.
                </p>
                <div className="flex flex-wrap justify-center gap-4">
                  <Link 
                    to="/about" 
                    className="inline-flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-cyan-600 to-blue-600 text-white rounded-xl font-medium hover:from-cyan-700 hover:to-blue-700 transition-all duration-300"
                  >
                    Learn About Our Work
                  </Link>
                  <Link 
                    to="/contact" 
                    className="inline-flex items-center gap-2 px-8 py-3 bg-white text-slate-700 rounded-xl font-medium border border-slate-200 hover:border-cyan-300 hover:bg-cyan-50 transition-all duration-300"
                  >
                    Get Involved
                  </Link>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Leadership;
